import { useMemo } from 'react';
import type { SxProps, Theme } from '@mui/material/styles';
import { Box, Slider, Stack, Typography } from '@/primitives';
import { DEFAULT_CURRENCY_SYMBOL } from '@/lib/currency';
import BreedAverageEbvCell from '@/components/publicSearch/animal/ebv/BreedAverageEbvCell';
import { coerceBreedAverageRaw } from '@/components/publicSearch/animal/ebv/breedAverageDisplay';
import { toSxArray } from '@/lib/sx';

export interface PercentileSliderProps {
  minPercentile?: number | string;
  maxPercentile?: number | string;
  ebvAverage?: string | number;
  /** Society EBV_FIELDS `format` (0/1/2/$) for breed average display. */
  ebvAverageFormat?: string;
  currencySymbol?: string;
  trackColor?: string;
  /** Society EBV_FIELDS low-axis label (worse percentile rank — slider left). */
  axisLeftLabel?: string;
  /** Society EBV_FIELDS high-axis label (better percentile rank — slider right). */
  axisRightLabel?: string;
}

/** Prefer text after " - " (e.g. "Gest. Len - Longer" → "Longer"); else first segment or full string. */
function shortenSocietyAxisLabel(label: string | undefined): string {
  if (label == null) return '';
  const trimmed = String(label).trim();
  if (!trimmed) return '';
  const parts = trimmed.split(' - ');
  return (parts[1] ?? parts[0] ?? '').trim() || trimmed;
}

const PERCENTILE_MIN = 1;
const PERCENTILE_MAX = 99;
/** Map BREEDPLAN rank (1 = best … 99 = worst) onto slider axis so best sits on the right. */
const SLIDER_FROM_RANK = (rank: number) =>
  PERCENTILE_MIN + PERCENTILE_MAX - rank;

export type PercentileSliderModel = {
  sliderValue: [number, number];
  hasValue: boolean;
  hasBreedAverage: boolean;
  formattedAverage: string | null;
  breedAverageRaw: string | number | null | undefined;
  breedAverageFormat: string;
  leftCaption: string;
  rightCaption: string;
  axisLeftLabel?: string;
  axisRightLabel?: string;
  accentColor?: string;
};

export function usePercentileSliderModel({
  minPercentile,
  maxPercentile,
  ebvAverage,
  ebvAverageFormat = '0',
  trackColor,
  axisLeftLabel,
  axisRightLabel,
}: PercentileSliderProps): PercentileSliderModel {
  const minValue = useMemo(() => {
    if (!minPercentile) return null;
    const num =
      typeof minPercentile === 'string'
        ? parseFloat(minPercentile)
        : minPercentile;
    if (isNaN(num)) return null;
    return Math.max(PERCENTILE_MIN, Math.min(PERCENTILE_MAX, Math.round(num)));
  }, [minPercentile]);

  const maxValue = useMemo(() => {
    if (!maxPercentile) return null;
    const num =
      typeof maxPercentile === 'string'
        ? parseFloat(maxPercentile)
        : maxPercentile;
    if (isNaN(num)) return null;
    return Math.max(PERCENTILE_MIN, Math.min(PERCENTILE_MAX, Math.round(num)));
  }, [maxPercentile]);

  const sliderValue = useMemo(() => {
    let rankLo: number;
    let rankHi: number;

    if (minValue !== null && maxValue !== null) {
      rankLo = Math.min(minValue, maxValue);
      rankHi = Math.max(minValue, maxValue);
    } else if (minValue !== null) {
      rankLo = PERCENTILE_MIN;
      rankHi = minValue;
    } else if (maxValue !== null) {
      rankLo = PERCENTILE_MIN;
      rankHi = maxValue;
    } else {
      rankLo = PERCENTILE_MIN;
      rankHi = PERCENTILE_MAX;
    }

    const sLo = SLIDER_FROM_RANK(rankHi);
    const sHi = SLIDER_FROM_RANK(rankLo);
    return [sLo, sHi] as [number, number];
  }, [minValue, maxValue]);

  const hasValue = minValue !== null || maxValue !== null;

  const breedAverageRaw = useMemo(
    () => coerceBreedAverageRaw(ebvAverage),
    [ebvAverage],
  );

  const formattedAverage = useMemo(() => {
    if (
      breedAverageRaw === null ||
      breedAverageRaw === undefined ||
      breedAverageRaw === ''
    ) {
      return null;
    }
    return String(breedAverageRaw).trim() === '' ? null : 'present';
  }, [breedAverageRaw]);

  const hasBreedAverage = formattedAverage !== null;

  const breedAverageFormat = ebvAverageFormat || '0';

  const leftCaption = useMemo(() => {
    const s = shortenSocietyAxisLabel(axisLeftLabel);
    return s || 'Low';
  }, [axisLeftLabel]);

  const rightCaption = useMemo(() => {
    const s = shortenSocietyAxisLabel(axisRightLabel);
    return s || 'Top';
  }, [axisRightLabel]);

  return {
    sliderValue,
    hasValue,
    hasBreedAverage,
    formattedAverage,
    breedAverageRaw,
    breedAverageFormat,
    leftCaption,
    rightCaption,
    axisLeftLabel,
    axisRightLabel,
    accentColor: trackColor ?? undefined,
  };
}

type TrackWidthMode = 'fluid' | 'fixed';

export function PercentileSliderTrack({
  model,
  widthMode = 'fluid',
}: {
  model: PercentileSliderModel;
  widthMode?: TrackWidthMode;
}) {
  const {
    sliderValue,
    hasValue,
    leftCaption,
    rightCaption,
    axisLeftLabel,
    axisRightLabel,
    accentColor,
  } = model;

  const stackSx =
    widthMode === 'fixed'
      ? {
          gap: 0,
          flex: '0 0 250px',
          width: 250,
          minWidth: 0,
        }
      : {
          gap: 0,
          flex: { xs: '1 1 auto', md: '0 0 250px' },
          minWidth: 0,
        };

  return (
    <Stack sx={stackSx}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          width: '100%',
          pb: 0,
        }}
      >
        <Typography
          variant="labelmedium"
          title={axisLeftLabel?.trim() || undefined}
          sx={{
            fontSize: '12px',
            color: 'text.secondary',
            maxWidth: '45%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            lineHeight: 1.2,
            mb: 0.5,
          }}
        >
          {leftCaption}
        </Typography>
        <Typography
          variant="labelmedium"
          title={axisRightLabel?.trim() || undefined}
          sx={{
            fontSize: '12px',
            color: 'text.secondary',
            maxWidth: '45%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            textAlign: 'right',
            lineHeight: 1.2,
            mb: 0.5,
          }}
        >
          {rightCaption}
        </Typography>
      </Box>
      <Slider
        disabled
        value={sliderValue}
        min={PERCENTILE_MIN}
        max={PERCENTILE_MAX}
        sx={(theme) => ({
          width: '100%',
          mt: -0.75,
          opacity: hasValue ? 1 : 0.3,
          '& .MuiSlider-track': {
            backgroundColor: hasValue
              ? (accentColor ?? (theme.vars || theme).palette.primary.main)
              : (theme.vars || theme).palette.grey[400],
            border: 'none',
          },
          '& .MuiSlider-thumb': {
            backgroundColor: hasValue
              ? (accentColor ?? (theme.vars || theme).palette.primary.main)
              : (theme.vars || theme).palette.grey[400],
            width: 16,
            height: 16,
            '&:hover': {
              boxShadow: 'none',
            },
            '&.Mui-disabled': {
              boxShadow: 'none',
            },
          },
          '& .MuiSlider-rail': {
            backgroundColor: (theme.vars || theme).palette.grey[300],
            opacity: 1,
          },
        })}
        marks={false}
      />
    </Stack>
  );
}

export function PercentileBreedAverageCell({
  formattedAverage,
  breedAverageRaw,
  breedAverageFormat,
  currencySymbol = DEFAULT_CURRENCY_SYMBOL,
  sx,
}: {
  formattedAverage: string | null;
  breedAverageRaw?: string | number | null;
  breedAverageFormat?: string;
  currencySymbol?: string;
  sx?: SxProps<Theme>;
}) {
  if (formattedAverage === null || formattedAverage === '') {
    return null;
  }

  return (
    <Stack
      sx={[
        {
          gap: '2px',
          flexShrink: 0,
          alignItems: 'flex-end',
          textAlign: 'right',
        },
        ...toSxArray(sx),
      ]}
    >
      <Typography
        variant="labelmedium"
        sx={{
          fontSize: '12px',
          color: 'text.secondary',
          textAlign: 'right',
        }}
      >
        Breed Avg
      </Typography>
      <BreedAverageEbvCell
        raw={breedAverageRaw}
        format={breedAverageFormat || '0'}
        currencySymbol={currencySymbol}
      />
    </Stack>
  );
}
